"use client";

import { useRef, useState, useCallback, useEffect } from "react";
import { ArrowLeftRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Automation() {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: panelRef,
    isVisible: panelVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const updatePosition = useCallback((clientX: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, x / rect.width * 100));
    setPosition(pct);
  }, []);
  const handlePointerDown = (e: React.PointerEvent) => {
    isDraggingRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updatePosition(e.clientX);
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    updatePosition(e.clientX);
  };
  const handlePointerUp = (e: React.PointerEvent) => {
    isDraggingRef.current = false;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {}
  };
  useEffect(() => {
    const onUp = () => {
      isDraggingRef.current = false;
    };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, []);
  const stats = [{
    value: "85% LESS",
    label: "MANUAL WORK"
  }, {
    value: "24/7 ACTIVE",
    label: "AI AGENTS"
  }, {
    value: "100% SCALE",
    label: "OPERATIONS"
  }];
  return <section data-section-id="1205" id="why-choose-ai" className="bg-background text-foreground pt-36 pb-24 md:pt-44 md:pb-36 px-6 md:px-10 lg:px-16 relative overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        {}
        <div className="mb-12 md:mb-20 flex items-center gap-3">
          <span className="font-mono text-[12px] tracking-[0.18em] uppercase text-foreground" style={{
          fontWeight: 500
        }}>
            § 01 / 02 — THE AUTONOMY SHIFT
          </span>
          <span className="block w-6 h-[3px] bg-foreground" />
        </div>

        {}
        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24 transition-all duration-[560ms] ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(20px)",
        transitionTimingFunction: "cubic-bezier(0.2, 0.7, 0.2, 1)"
      }}>
          <div className="lg:col-span-5">
            <div className="font-mono text-xs md:text-sm tracking-[0.18em] uppercase text-foreground/70 mb-6 font-medium">
              FROM MANUAL TO AUTONOMOUS
            </div>
            <h2 className="font-default font-black uppercase leading-[0.92] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.5rem, 6vw, 5.5rem)"
          }}>
              SEE THE{" "}
              <span className="inline-block relative align-baseline">
                <span className="inline-block px-3 py-1 border-[3px] border-foreground bg-foreground/70 text-foreground" style={{
                transform: "rotate(-3deg)",
                boxShadow: "4px 4px 0 hsl(220 18% 8%)",
                color: "hsl(220 20% 2%)"
              }}>
                  EVOLUTION
                </span>
              </span>
              .
            </h2>
          </div>

          <div className="lg:col-span-7 flex items-end">
            <p className="font-default text-foreground/85" style={{
            fontSize: "clamp(1.0625rem, 1.25vw, 1.25rem)",
            lineHeight: 1.5
          }}>
              Drag the slider to witness the transformation of your business operations. On the left, the fragmented, manual workflows that drain your resources. On the right, the streamlined, AI-automated ecosystem that runs with surgical precision. We replace human bottlenecks with intelligent agents, ensuring your business scales without the friction of traditional growth.
            </p>
          </div>
        </div>

        {}
        <div ref={panelRef} className="relative transition-all duration-[560ms] ease-out" style={{
        opacity: panelVisible ? 1 : 0,
        transform: panelVisible ? "translateY(0)" : "translateY(20px)",
        transitionTimingFunction: "cubic-bezier(0.2, 0.7, 0.2, 1)"
      }}>
          {}
          <div aria-hidden="true" className="absolute -top-4 -left-4 w-full h-full pointer-events-none" style={{
          backgroundImage: "radial-gradient(hsl(215 15% 55%) 1.5px, transparent 1.5px)",
          backgroundSize: "10px 10px",
          opacity: 0.55
        }} />

          <div ref={containerRef} className="relative border-[3px] border-foreground overflow-hidden select-none touch-none bg-background" style={{
          aspectRatio: "16 / 9"
        }} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp}>
            {}
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automated_workflow_dashboard.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automated_workflow_dashboard.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automated_workflow_dashboard.png-wvc-sizes" alt="Automated AI dashboard showing streamlined workflows" className="absolute inset-0 w-full h-full object-cover pointer-events-none" loading="lazy" decoding="async" draggable={false} />

            {}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{
            width: `${position}%`
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/manual_cluttered_office.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/manual_cluttered_office.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/manual_cluttered_office.webp-wvc-sizes" alt="Manual cluttered office environment" className="absolute inset-0 h-full object-cover" style={{
              width: `${100 / position * 100}%`,
              maxWidth: "none"
            }} loading="lazy" decoding="async" draggable={false} />
            </div>

            {}
            <div className="absolute top-4 md:top-6 left-4 md:left-6 z-20 pointer-events-none" style={{
            transform: "rotate(-4deg)"
          }}>
              <span className="inline-block px-3 py-1 md:px-4 md:py-1.5 border-[3px] border-foreground font-default font-black uppercase tracking-wider text-sm md:text-base" style={{
              backgroundColor: "hsl(220 15% 40%)",
              color: "hsl(0 0% 98%)",
              boxShadow: "4px 4px 0 hsl(220 18% 8%)"
            }}>
                MANUAL
              </span>
            </div>

            {}
            <div className="absolute top-4 md:top-6 right-4 md:right-6 z-20 pointer-events-none" style={{
            transform: "rotate(3deg)"
          }}>
              <span className="inline-block px-3 py-1 md:px-4 md:py-1.5 border-[3px] border-foreground font-default font-black uppercase tracking-wider text-sm md:text-base" style={{
              backgroundColor: "hsl(215 15% 55%)",
              color: "hsl(220 20% 2%)",
              boxShadow: "4px 4px 0 hsl(220 18% 8%)"
            }}>
                AUTOMATED
              </span>
            </div>

            {}
            <div className="absolute top-0 bottom-0 z-30 pointer-events-none" style={{
            left: `${position}%`,
            transform: "translateX(-50%)",
            width: "4px",
            backgroundColor: "hsl(215 15% 55%)"
          }}>
              {}
              <div className="absolute left-1/2 top-1/2 pointer-events-auto cursor-ew-resize flex items-center justify-center border-[3px] border-foreground" style={{
              transform: "translate(-50%, -50%)",
              width: "clamp(48px, 6vw, 64px)",
              height: "clamp(48px, 6vw, 64px)",
              backgroundColor: "hsl(215 15% 55%)",
              borderRadius: "9999px",
              boxShadow: "4px 4px 0 hsl(220 18% 8%)"
            }} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} role="slider" aria-label="Drag to compare manual vs automated" aria-valuenow={Math.round(position)} aria-valuemin={0} aria-valuemax={100}>
                <ArrowLeftRight className="text-foreground" style={{
                width: "55%",
                height: "55%",
                strokeWidth: 2.5
              }} />
              </div>
            </div>

            {}
            <style>{`
              @media (max-width: 767px) {
                #why-choose-ai [data-comparison-panel] {
                  aspect-ratio: 4 / 5 !important;
                }
              }
            `}</style>
          </div>
        </div>

        {}
        <div ref={statsRef} className="mt-16 md:mt-24 border-y-[3px] border-foreground transition-all duration-[560ms] ease-out" style={{
        opacity: statsVisible ? 1 : 0,
        transform: statsVisible ? "translateY(0)" : "translateY(20px)",
        transitionTimingFunction: "cubic-bezier(0.2, 0.7, 0.2, 1)"
      }}>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y-[3px] md:divide-y-0 md:divide-x-[3px] divide-foreground">
            {stats.map((stat, i) => <div key={i} data-index={i} className="py-8 md:py-10 px-6 flex flex-col items-center md:items-start gap-2">
                <span className="font-mono font-bold text-foreground" style={{
              fontSize: "clamp(1.5rem, 2.5vw, 2.25rem)",
              letterSpacing: "0.02em"
            }}>
                  {stat.value}
                </span>
                <span className="font-mono uppercase text-foreground/70 tracking-[0.18em]" style={{
              fontSize: "0.75rem",
              fontWeight: 500
            }}>
                  {stat.label}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}